import { useRef, useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import EmailEditor, { EditorRef, EmailEditorProps } from 'react-email-editor';
import { ArrowLeft, Save, Copy, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { MERGE_TAGS, TIPO_LABEL, type TipoDocumento } from './template-types';
import { usePlantillasConfig, useUpdatePlantillaConfig } from './usePlantillaConfig';

function EtiquetaChip({ tag, desc }: { tag: string; desc: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(tag);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={copy}
      className="flex items-center justify-between gap-2 w-full px-3 py-2 rounded-lg border border-gray-100 hover:border-primary-200 hover:bg-primary-50 transition-colors text-left group"
    >
      <div>
        <p className="text-xs font-mono font-medium text-primary-700">{tag}</p>
        <p className="text-xs text-text-secondary">{desc}</p>
      </div>
      {copied
        ? <Check size={13} className="text-green-600 flex-shrink-0" />
        : <Copy size={13} className="text-gray-300 group-hover:text-primary-400 flex-shrink-0" />}
    </button>
  );
}

function LoopSnippetBox({ label, snippet }: { label: string; snippet: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="rounded-lg border border-gray-100 p-2 space-y-1.5">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-text-primary">{label}</p>
        <button onClick={copy} className="flex items-center gap-1 text-[10.5px] text-primary-600 hover:text-primary-800">
          {copied ? <><Check size={11} /> Copiado</> : <><Copy size={11} /> Copiar</>}
        </button>
      </div>
      <p className="text-[10.5px] text-text-secondary leading-snug">
        Pega este bloque en un elemento "HTML" de Unlayer. Se repetirá una fila por cada item real.
      </p>
      <pre className="text-[10px] bg-gray-50 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all text-text-secondary">{snippet}</pre>
    </div>
  );
}

export function PersonalizadaEditorPage() {
  const { tipo } = useParams<{ tipo: TipoDocumento }>();
  const navigate = useNavigate();
  const tipoDocumento = (tipo || 'receta') as TipoDocumento;

  const editorRef = useRef<EditorRef>(null);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const [editorReady, setEditorReady] = useState(false);
  const [editorHeight, setEditorHeight] = useState(600);
  const [saving, setSaving] = useState(false);

  const { data: config } = usePlantillasConfig();
  const updateMutation = useUpdatePlantillaConfig();

  useEffect(() => {
    const el = editorContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setEditorHeight(Math.floor(entry.contentRect.height));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const onReady: EmailEditorProps['onReady'] = useCallback(() => {
    setEditorReady(true);
  }, []);

  useEffect(() => {
    const disenoJson = config?.[tipoDocumento]?.disenoPersonalizadoJson;
    if (editorReady && disenoJson) {
      try {
        editorRef.current?.editor?.loadDesign(JSON.parse(disenoJson));
      } catch {
        // diseño inválido, arranca en blanco
      }
    }
  }, [editorReady, config, tipoDocumento]);

  const handleSave = () => {
    if (!editorReady) { toast.error('El editor aún no está listo'); return; }
    setSaving(true);
    editorRef.current?.editor?.exportHtml((data) => {
      const actual = config?.[tipoDocumento];
      updateMutation.mutate(
        {
          tipo: tipoDocumento,
          patch: {
            diseno: 'personalizada',
            tamanoHoja: actual?.tamanoHoja ?? 'a4',
            orientacion: actual?.orientacion ?? 'vertical',
            disenoPersonalizadoJson: JSON.stringify(data.design),
            disenoPersonalizadoHtml: data.html,
          },
        },
        {
          onSuccess: () => { toast.success('Diseño personalizado guardado'); navigate('/configuracion/plantillas'); },
          onError: () => toast.error('Error al guardar el diseño'),
          onSettled: () => setSaving(false),
        },
      );
    });
  };

  const tags = MERGE_TAGS[tipoDocumento];

  return (
    <div className="-m-5 flex flex-col" style={{ height: 'calc(100% + 40px)' }}>
      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 bg-white flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/configuracion/plantillas')} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary">
            <ArrowLeft size={18} />
          </button>
          <h2 className="font-semibold text-text-primary">
            Diseño personalizado — {TIPO_LABEL[tipoDocumento]}
          </h2>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || updateMutation.isPending || !editorReady}
          className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Guardar diseño
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden min-h-0">
        <div className="w-72 flex-shrink-0 border-r border-gray-100 bg-white overflow-y-auto p-4 space-y-5">
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Etiquetas dinámicas</h3>
            <p className="text-xs text-text-secondary">Haz clic para copiar e inserta en el editor</p>
            <div className="space-y-1.5">
              {tags.simples.map(e => <EtiquetaChip key={e.tag} {...e} />)}
            </div>
          </div>

          {tags.loop && (
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Listas repetibles</h3>
              <LoopSnippetBox label={tags.loop.label} snippet={tags.loop.snippet} />
            </div>
          )}
        </div>

        <div ref={editorContainerRef} className="relative flex-1 overflow-hidden">
          {!editorReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
              <div className="flex items-center gap-2 text-text-secondary">
                <Loader2 size={20} className="animate-spin" />
                <span className="text-sm">Cargando editor...</span>
              </div>
            </div>
          )}
          <EmailEditor
            ref={editorRef}
            onReady={onReady}
            style={{ height: editorHeight }}
            options={{
              projectId: 197411,
              locale: 'es-ES',
              features: { 
                preview: true
              },
              displayMode: 'web',
              tools:{
                // 1. Herramientas activas (Base Documento + HTML)
                text: { enabled: true },
                heading: { enabled: true },
                button: { enabled: true },
                image: { enabled: true },
                divider: { enabled: true },
                social: { enabled: true },
                html: { enabled: true, position: 1 }, // Habilitar HTML explícitamente

                // 2. Herramientas nativas de Web que se apagan
                form: { enabled: false },
                video: { enabled: false },
                menu: { enabled: false },
                carousel: { enabled: false },
                timer: { enabled: false },
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}
